/**
 * DashboardCallEmptyState Component
 * Shows when the Whereby call panel is collapsed
 * Provides CTA to expand and start the team call
 * Shared component used by both gamer and facilitator features
 */

import React from 'react'
import { Box, Typography, Button, Tooltip } from '@mui/material'
import { PhoneIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '../../hooks/useIntractionTranslate'
import { INTERAX_COLORS } from '../../../constants/colors'
import { useTeamWherebyMeeting } from '../../../apiLayer/queryHooks/queries'
import useAppStateSession from '../../../hooks/useAppStateSession'

interface DashboardCallEmptyStateProps {
  // The team resolved from the current upcoming activity (passed by the parent). We must never infer a
  // team from user membership here — that accumulates teams across sprints and would query the wrong
  // sprint's room. No current activity → no teamId → no room status.
  teamId?: string
}

export const DashboardCallEmptyState: React.FC<DashboardCallEmptyStateProps> = ({ teamId }) => {
  const { t } = useTranslation()
  const { setState } = useAppStateSession()

  const onClick = () => {
    setState((state) => {
      return { ...state, shouldShowCall: true }
    })
  }

  // Whether the current activity's team has an active room (or none yet).
  const { data: meetingResponse } = useTeamWherebyMeeting(teamId)
  const isActive = !!meetingResponse?.data

  // Determine which message to show based on meeting status
  const headerKey = isActive ? 'gamer.voiceCall.startTeamVoiceCall' : 'gamer.voiceCall.joinTeamCallFirst'

  const descriptionKey = isActive ? 'gamer.voiceCall.connectWithTeammates' : 'gamer.voiceCall.teamCallRequirement'

  const ctaKey = isActive ? 'gamer.voiceCall.startCall' : 'gamer.voiceCall.startTeamCallAndJoin'

  const tooltipKey = isActive ? 'gamer.voiceCall.emptyRoomTooltip' : 'gamer.voiceCall.teamCallTooltip'

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '400px',
        p: 2,
        borderRadius: '10px',
        backgroundColor: `${INTERAX_COLORS.heritageBlue}66`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {/* Inner container with radial background */}
      <Box
        sx={{
          width: '100%',
          height: '100%',
          p: 4,
          borderRadius: '10px',
          background: `radial-gradient(50.19% 50.19%, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%), ${INTERAX_COLORS.heritageBlue}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: `${INTERAX_COLORS.sun}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <PhoneIcon style={{ width: '40px', height: '40px', color: INTERAX_COLORS.sun }} />
        </Box>

        {/* Header */}
        <Typography
          variant="h5"
          sx={{
            color: INTERAX_COLORS.white,
            fontWeight: 600,
            textAlign: 'center',
            maxWidth: '350px'
          }}
        >
          {t(headerKey)}
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            color: INTERAX_COLORS.thunderGrey,
            textAlign: 'center',
            maxWidth: '350px',
            lineHeight: 1.6
          }}
        >
          {t(descriptionKey)}
        </Typography>

        {/* CTA Button with Tooltip */}
        <Tooltip title={t(tooltipKey)} arrow>
          <Button
            variant="contained"
            onClick={onClick}
            startIcon={<PhoneIcon style={{ width: '20px', height: '20px' }} />}
            sx={{
              backgroundColor: INTERAX_COLORS.sun,
              color: INTERAX_COLORS.offBlack,
              fontWeight: 600,
              px: 4,
              py: 1.5,
              borderRadius: '8px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: `${INTERAX_COLORS.sun}cc`
              }
            }}
          >
            {t(ctaKey)}
          </Button>
        </Tooltip>

        {/* Info Icon with Tooltip */}
        <Tooltip title={t(tooltipKey)} arrow>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'help',
              color: INTERAX_COLORS.thunderGrey,
              '&:hover': {
                color: INTERAX_COLORS.sun
              }
            }}
          >
            <InformationCircleIcon style={{ width: '20px', height: '20px' }} />
            <Typography variant="caption">{t('common.whyDoINeedThis')}</Typography>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  )
}
